import React, { useCallback } from "react";
import Button from "@scripts/react/components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";

interface AutoAdvanceDotsProps {
    stepsCount: number;
    index: number;
    onIndexChange: (index: number) => void;
    className?: string;
}

export function AutoAdvanceDots(props: AutoAdvanceDotsProps) {
    const { stepsCount, index, onIndexChange, className } = props;

    const goTo = useCallback(
        (i: number) => {
            const clamped = Math.max(0, Math.min(stepsCount - 1, i));
            onIndexChange(clamped);
        },
        [onIndexChange, stepsCount]
    );

    const next = useCallback(() => {
        const nextIdx = index >= stepsCount - 1 ? 0 : index + 1;
        onIndexChange(nextIdx);
    }, [index, stepsCount, onIndexChange]);

    const prev = useCallback(() => {
        const prevIdx = index <= 0 ? stepsCount - 1 : index - 1;
        onIndexChange(prevIdx);
    }, [index, stepsCount, onIndexChange]);

    return (
        <div className={twClassNames("flex items-center justify-center", className)}>
            {/* @ts-expect-error - Button component type issue */}
            <Button
                onClick={prev}
                aria-label="Previous step"
                type="ghost"
                size="xsmall"
                shape="square"
            >
                <i className="far fa-arrow-left" />
            </Button>
            <div className="flex items-center justify-center">
                {Array.from({ length: stepsCount }).map((_, i) => {
                    const isCurrent = i === index;
                    return (
                        <button
                            key={i}
                            aria-label={`Go to Step ${i + 1}`}
                            title={`Go to Step ${i + 1}`}
                            className="group/dot relative flex h-5 w-5 items-center justify-center focus:outline-none"
                            onClick={() => goTo(i)}
                        >
                            <span
                                className={twClassNames(
                                    "h-2.5 w-2.5 rounded-full transition-colors",
                                    isCurrent
                                        ? "bg-tertiary"
                                        : "bg-gray-300 group-hover/dot:bg-gray-400"
                                )}
                            />
                        </button>
                    );
                })}
            </div>
            {/* @ts-expect-error - Button component type issue */}
            <Button onClick={next} aria-label="Next step" type="ghost" size="xsmall" shape="square">
                <i className="far fa-arrow-right" />
            </Button>
        </div>
    );
}
